/** biome-ignore-all lint/suspicious/noEmptyBlockStatements: allow empty functions as default values */
/** biome-ignore-all lint/suspicious/noExplicitAny: allow any */
import {createContext, type FC, type ReactNode, useCallback, useMemo, useReducer} from 'react';

interface HeaderState {
  title: string;
  saveButtons: boolean;
  preview: boolean;
  hiddenHeader: boolean;
  headerOptions: boolean;
  headerClass: string;
}

type HeaderAction =
  | {type: 'SET_TITLE'; payload: string}
  | {type: 'SET_SAVE_BUTTONS'; payload: boolean}
  | {type: 'SET_PREVIEW'; payload: boolean}
  | {type: 'SET_HIDDEN_HEADER'; payload: boolean}
  | {type: 'SET_HEADER_OPTIONS'; payload: boolean}
  | {type: 'SET_HEADER_CLASS'; payload: string};

interface OneStepContextType {
  activeSections: {
    design: boolean;
    gifting: boolean;
    details: boolean;
  };
  header: HeaderState & {
    setTitle: (title: string) => void;
    setSaveButtons: (saveButtons: boolean) => void;
    setPreview: (preview: boolean) => void;
    setHeaderClass: (headerClass: string) => void;
    setHidden: (hidden: boolean) => void;
    setHeaderOptions: (headerOptions: boolean) => void;
  };
}

const defaultContextValue: OneStepContextType = {
  activeSections: {
    design: true,
    gifting: true,
    details: true,
  },
  header: {
    title: '',
    setTitle: () => {},
    saveButtons: false,
    setSaveButtons: () => {},
    preview: false,
    setPreview: () => {},
    headerClass: '',
    setHeaderClass: () => {},
    hiddenHeader: false,
    setHidden: () => {},
    headerOptions: true,
    setHeaderOptions: () => {},
  },
};

const headerReducer = (state: HeaderState, action: HeaderAction): HeaderState => {
  switch (action.type) {
    case 'SET_TITLE':
      return {...state, title: action.payload};
    case 'SET_SAVE_BUTTONS':
      return {...state, saveButtons: action.payload};
    case 'SET_PREVIEW':
      return {...state, preview: action.payload};
    case 'SET_HIDDEN_HEADER':
      return {...state, hiddenHeader: action.payload};
    case 'SET_HEADER_OPTIONS':
      return {...state, headerOptions: action.payload};
    case 'SET_HEADER_CLASS':
      return {...state, headerClass: action.payload};
    default:
      return state;
  }
};

export const OneStepContext = createContext<OneStepContextType>(defaultContextValue);

interface OneStepContextParentProps {
  children: ReactNode;
}

export const OneStepContextParent: FC<OneStepContextParentProps> = ({children}) => {
  const initialHeaderState: HeaderState = {
    title: '',
    saveButtons: false,
    preview: false,
    hiddenHeader: false,
    headerOptions: true,
    headerClass: '',
  };

  const [headerState, dispatch] = useReducer(headerReducer, initialHeaderState);

  const setTitle = useCallback((title: string) => {
    dispatch({type: 'SET_TITLE', payload: title});
  }, []);

  const setSaveButtons = useCallback((saveButtons: boolean) => {
    dispatch({type: 'SET_SAVE_BUTTONS', payload: saveButtons});
  }, []);

  const setPreview = useCallback((preview: boolean) => {
    dispatch({type: 'SET_PREVIEW', payload: preview});
  }, []);

  const setHidden = useCallback((hidden: boolean) => {
    dispatch({type: 'SET_HIDDEN_HEADER', payload: hidden});
  }, []);

  const setHeaderOptions = useCallback((headerOptions: boolean) => {
    dispatch({type: 'SET_HEADER_OPTIONS', payload: headerOptions});
  }, []);

  const setHeaderClass = useCallback((headerClass: string) => {
    dispatch({type: 'SET_HEADER_CLASS', payload: headerClass});
  }, []);

  const contextValue: OneStepContextType = useMemo(
    () => ({
      activeSections: {
        design: true,
        gifting: true,
        details: true,
      },
      header: {
        ...headerState,
        setTitle,
        setSaveButtons,
        setPreview,
        setHeaderClass,
        setHidden,
        setHeaderOptions,
      },
    }),
    [headerState, setTitle, setSaveButtons, setPreview, setHeaderClass, setHidden, setHeaderOptions]
  );

  return <OneStepContext.Provider value={contextValue}>{children}</OneStepContext.Provider>;
};
